/**
 * "My Visit" — every vehicle this device has opened during THIS visit.
 *
 * Automatic and passive: scanning a card puts it here, with no decision asked
 * of the shopper. That is what makes it different from `compareStore`, which is
 * an explicit "add to comparison" action and stays untouched — the two are not
 * merged, because a passive breadcrumb trail and a deliberate shortlist answer
 * different questions.
 *
 * ISOLATED PER DEALERSHIP, reusing compareStore's active-org guard: a shopper
 * who scans cards at dealer A and then at dealer B must never see A's vehicles
 * in B's tray.
 *
 * sessionStorage, matching the visit's own lifecycle — survives navigating
 * between vehicles, ends when the tab genuinely closes.
 */
const KEY = (orgId) => `yot_visit_vehicles_${orgId}`;
const ACTIVE_ORG = 'yot_visit_active_org';

const read = (k) => {
    try { return JSON.parse(sessionStorage.getItem(k) || '[]'); } catch { return []; }
};

const write = (k, list) => {
    try { sessionStorage.setItem(k, JSON.stringify(list)); } catch { /* private mode */ }
};

export const visitStore = {
    list(orgId) { return orgId ? read(KEY(orgId)) : []; },

    count(orgId) { return this.list(orgId).length; },

    /**
     * Remember this vehicle. A snapshot of the label and thumbnail is stored
     * with it so the tray can render every earlier vehicle without a second
     * round of API calls — the tray must be instant, it is a way back.
     */
    add(orgId, entry) {
        if (!orgId || !entry?.card_id) return this.list(orgId);
        const list = this.list(orgId).filter((v) => v.card_id !== entry.card_id);
        list.push({
            card_id: entry.card_id,
            label: entry.label || null,
            photo: entry.photo || null,
            price: entry.price ?? null,
            at: new Date().toISOString(),
        });
        write(KEY(orgId), list);
        this.setActiveOrg(orgId);
        return list;
    },

    remove(orgId, cardId) {
        if (!orgId) return;
        write(KEY(orgId), this.list(orgId).filter((v) => v.card_id !== cardId));
    },

    clear(orgId) { if (orgId) { try { sessionStorage.removeItem(KEY(orgId)); } catch { /* noop */ } } },

    activeOrg() { try { return sessionStorage.getItem(ACTIVE_ORG) || ''; } catch { return ''; } },

    setActiveOrg(orgId) {
        if (!orgId) return;
        try { sessionStorage.setItem(ACTIVE_ORG, orgId); } catch { /* noop */ }
    },
};

export default visitStore;
